// /projects/:projectID/issues — issue list with filters + infinite scroll (T032).
// Branch filter via URL ?branch=<branchId> added in T058.
//
// Surface of this page:
//   - Header: project breadcrumb back to /projects/:id, page title + count.
//   - Filter bar: severity chips (multi-toggle), kind chips (multi-toggle),
//     new-code tri-state (All / New / Overall), branch chip (read-only,
//     cleared by clicking ×). Filter state is mirrored in the URL via
//     `?severity=…&kind=…&new=…&branch=…` so deep links survive refresh
//     and the back button reverses filter changes the way users expect.
//   - Result list: each row is severity chip + kind chip + message + file:line
//     + relative detected_at. New code rows get a subtle "new" pip so the
//     filter toggle is verifiable at a glance.
//   - Infinite scroll: an IntersectionObserver sentinel at the bottom of the
//     list triggers `useSWRInfinite`'s setSize. The backend's `next_cursor`
//     is the source of truth — an empty cursor stops paging, and we render
//     a "you've reached the end" footer so users can tell scroll-stop from
//     a hung load.

import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import {
  AlertTriangle,
  ArrowLeft,
  Bug,
  Eye,
  FileUp,
  FileWarning,
  Filter,
  GitBranch,
  KeyRound,
  Loader2,
  ShieldAlert,
  Sparkles,
  Sprout,
  Wrench,
} from 'lucide-react';
import {
  ApiError,
  Branch,
  CLEAN_CODE_ATTRIBUTES,
  CleanCodeAttribute,
  ISSUE_KINDS,
  ISSUE_SEVERITIES,
  Issue,
  IssueKind,
  IssueListParams,
  IssueListResponse,
  IssueNewCodeFilter,
  IssueSeverity,
  Project,
  SOFTWARE_QUALITIES,
  SoftwareQuality,
  ISSUE_CONTEXTS,
  type IssueContext,
  buildIssuesURL,
  fetcher,
  jiraApi,
} from '../lib/api';
import { IssueDetailDrawer } from '../components/IssueDetailDrawer';
import { ExternalIssueImportDialog } from '../components/ExternalIssueImportDialog';
import { PageHeader } from '../components/ui';

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50; // matches db.DefaultIssueListLimit

/** Set or delete a single URL search param and return the updated URLSearchParams. */
function applySearchParam(
  current: URLSearchParams,
  key: string,
  value: string | undefined,
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (value) next.set(key, value);
  else next.delete(key);
  return next;
}

/** Map an IssueNewCodeFilter value to the boolean `is_new` API param. */
function newCodeFilterToIsNew(filter: IssueNewCodeFilter): boolean | undefined {
  if (filter === 'new') return true;
  if (filter === 'overall') return false;
  return undefined;
}

export function Issues() {
  const { projectID } = useParams<{ projectID: string }>();
  const id = projectID ?? '';

  // Filter state lives in the URL — that way refresh, share, and back button
  // all "just work" without a separate state-sync layer. URLSearchParams is
  // the source of truth; setters call setSearch to update both the URL and
  // the local view at the same time.
  const [search, setSearch] = useSearchParams();
  const severityFilter = parseSeverity(search.get('severity'));
  const kindFilter = parseKind(search.get('kind'));
  const newCodeFilter = parseNewCode(search.get('new'));
  const softwareQualityFilter = parseSoftwareQuality(search.get('quality'));
  const cleanCodeAttrFilter = parseCleanCodeAttr(search.get('attr'));
  const contextFilter = parseContext(search.get('context'));
  // Branch filter — set when the user navigated from ProjectDetail with a
  // branch selected in the BranchSwitcher (T058).
  const branchFilter = search.get('branch') ?? '';

  const setSeverity = (next: IssueSeverity | undefined) =>
    setSearch(applySearchParam(search, 'severity', next), { replace: true });

  const setKind = (next: IssueKind | undefined) =>
    setSearch(applySearchParam(search, 'kind', next), { replace: true });

  const setNewCode = (next: IssueNewCodeFilter) => {
    const params = new URLSearchParams(search);
    if (next === 'all') params.delete('new');
    else params.set('new', next);
    setSearch(params, { replace: true });
  };

  const setSoftwareQuality = (next: SoftwareQuality | undefined) =>
    setSearch(applySearchParam(search, 'quality', next), { replace: true });

  // T9079: production is the default, so it is the absence of the param.
  const setContext = (next: IssueContext) => {
    const params = new URLSearchParams(search);
    if (next === 'production') params.delete('context');
    else params.set('context', next);
    setSearch(params, { replace: true });
  };

  const setCleanCodeAttr = (next: CleanCodeAttribute | undefined) =>
    setSearch(applySearchParam(search, 'attr', next), { replace: true });

  // T592: one-click filter presets. Builds the full combined param set in a
  // single setSearch call — calling setKind/setNewCode/setSoftwareQuality in
  // sequence would each read the same stale `search` snapshot and clobber
  // each other, since none of those setters merge against a prior in-flight
  // update within the same synchronous handler.
  const applyPreset = (preset: 'maintainability' | 'new-code-smells') => {
    const params = new URLSearchParams(search);
    params.delete('attr');
    if (preset === 'maintainability') {
      params.delete('kind');
      params.delete('new');
      params.set('quality', 'Maintainability');
    } else {
      params.set('kind', 'CODE_SMELL');
      params.set('new', 'new');
      params.delete('quality');
    }
    setSearch(params, { replace: true });
  };

  const clearBranch = () => {
    const params = new URLSearchParams(search);
    params.delete('branch');
    setSearch(params, { replace: true });
  };

  // Drawer state — also mirrored in the URL (?issue=<id>) so deep links
  // open the drawer on the matching row and back/forward dismisses it.
  const selectedIssueID = search.get('issue') ?? '';
  const openIssue = (issueId: string) => {
    const params = new URLSearchParams(search);
    if (issueId) params.set('issue', issueId);
    else params.delete('issue');
    setSearch(params);
  };
  const closeIssue = () => {
    if (!search.has('issue')) return;
    const params = new URLSearchParams(search);
    params.delete('issue');
    setSearch(params, { replace: true });
  };

  // The parent-project lookup gives us the breadcrumb name + a fast 404
  // signal. We deliberately fetch it via SWR with the same URL the
  // ProjectDetail page uses, so navigating back/forth between Issues and
  // ProjectDetail is free.
  const projectKey = id ? `/api/v1/projects/${id}` : null;
  const project = useSWR<Project>(projectKey, fetcher);

  // Fetch branches to resolve the branch ID to a human-readable name.
  // The SWR cache is shared with ProjectDetail so this is a no-op network
  // request when the user navigated from the project page (T058).
  const branchesKey = id && branchFilter ? `/api/v1/projects/${id}/branches` : null;
  const branchesData = useSWR<Branch[]>(branchesKey, fetcher);
  const selectedBranchName = useMemo(() => {
    if (!branchFilter || !branchesData.data) return null;
    return branchesData.data.find((b) => b.id === branchFilter)?.name ?? null;
  }, [branchFilter, branchesData.data]);

  // Base filter — recomputed on every render but stable across renders
  // with the same logical filter state (useMemo keeps reference equality).
  const baseParams = useMemo<Omit<IssueListParams, 'cursor'>>(
    () => ({
      project_id: id,
      branch_id: branchFilter || undefined,
      severity: severityFilter,
      kind: kindFilter,
      is_new: newCodeFilterToIsNew(newCodeFilter),
      software_quality: softwareQualityFilter,
      clean_code_attribute: cleanCodeAttrFilter,
      path_class: contextFilter === 'all' ? undefined : contextFilter,
      limit: PAGE_SIZE,
    }),
    [id, branchFilter, severityFilter, kindFilter, newCodeFilter, softwareQualityFilter, cleanCodeAttrFilter, contextFilter],
  );

  // SWR Infinite — `getKey` returns the URL for page `index`. The previous
  // page's `next_cursor` is used to build page N+1's URL; an empty cursor
  // (after the first page) terminates the chain. Returning null makes
  // useSWRInfinite stop calling the fetcher, so we don't fire a request
  // past the end of the data.
  const getKey = useCallback(
    (index: number, prev: IssueListResponse | null): string | null => {
      if (!id) return null;
      if (index === 0) return buildIssuesURL(baseParams);
      if (!prev || !prev.next_cursor) return null;
      return buildIssuesURL({ ...baseParams, cursor: prev.next_cursor });
    },
    [id, baseParams],
  );

  const {
    data: pages,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
  } = useSWRInfinite<IssueListResponse>(getKey, fetcher, {
    revalidateFirstPage: false,
  });

  const issues: Issue[] = useMemo(
    () => (pages ?? []).flatMap((p) => p.items ?? []),
    [pages],
  );
  const lastPage = pages?.[pages.length - 1];
  const hasMore = Boolean(lastPage?.next_cursor);
  const isLoadingMore =
    isValidating && pages !== undefined && size > pages.length;

  // T592: whether any filter (beyond project/branch scoping) narrows the
  // result set. Drives both the empty-state copy and the effort-sum badge —
  // an unfiltered "everything" view isn't what "effort-sum on filtered sets"
  // means, so we only surface the sum once the user has actually filtered.
  const hasActiveFilters =
    severityFilter !== undefined ||
    kindFilter !== undefined ||
    newCodeFilter !== 'all' ||
    branchFilter !== '' ||
    softwareQualityFilter !== undefined ||
    cleanCodeAttrFilter !== undefined ||
    contextFilter !== 'production';

  // T592: effort-sum (person-time) over the currently loaded issues. This is
  // necessarily a partial sum when `hasMore` is true — infinite scroll only
  // materializes pages the user has scrolled past — so the badge is labelled
  // with a "+" suffix in that case, same convention as the "N shown" count.
  const totalEffortMin = useMemo(
    () => issues.reduce((sum, i) => sum + (i.effort_min ?? 0), 0),
    [issues],
  );

  // Resolve the URL-selected issue against the loaded pages. We deliberately
  // don't refetch a single issue when it isn't in the list: the Phase-1
  // backend doesn't expose GET /api/v1/issues/{id} yet, and the drawer's
  // own "not in current view" state communicates the deep-link miss
  // clearly. Once a single-issue endpoint lands, this lookup grows a
  // fallback fetch with minimal churn elsewhere.
  const selectedIssue = useMemo<Issue | null>(() => {
    if (!selectedIssueID) return null;
    return issues.find((i) => i.id === selectedIssueID) ?? null;
  }, [selectedIssueID, issues]);

  const [showImport, setShowImport] = useState(false);

  // Sentinel for infinite scroll. The observer fires when the sentinel is
  // ≥10% visible; we bump `size` to fetch the next page. The dep array
  // covers everything we read inside the callback so React doesn't warn.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!hasMore) return;
    if (isLoadingMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSize((s) => s + 1);
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, isLoadingMore, setSize]);

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <BackToProject projectID={id} projectName={project.data?.name} />
      <PageHeader
        eyebrow="orbital · issues"
        title="Issues"
        meta={
          issues.length > 0 ? (
            <span className="inline-flex items-baseline gap-3 flex-wrap">
              <span>
                {issues.length}
                {hasMore ? '+' : ''} shown
              </span>
              {hasActiveFilters ? (
                <span
                  className="orb-badge border bg-bg-inset text-fg-muted border-border"
                  title="Sum of remediation effort across the loaded issues in this filtered set"
                >
                  <Wrench className="h-3 w-3" aria-hidden="true" />
                  <span>
                    {formatEffort(totalEffortMin)}
                    {hasMore ? '+' : ''} effort
                  </span>
                </span>
              ) : null}
            </span>
          ) : null
        }
        description={
          <>
            Findings emitted by the analyzers, newest first. Filter by severity,
            kind, or scope to focus on what needs attention.
          </>
        }
        actions={
          <button
            type="button"
            onClick={() => setShowImport(true)}
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs
              font-semibold border border-border text-fg-muted hover:text-fg
              hover:border-fg-muted transition-colors
            "
          >
            <FileUp className="h-3.5 w-3.5" aria-hidden="true" />
            Import report
          </button>
        }
      />

      <FilterBar
        severity={severityFilter}
        kind={kindFilter}
        newCode={newCodeFilter}
        softwareQuality={softwareQualityFilter}
        cleanCodeAttr={cleanCodeAttrFilter}
        context={contextFilter}
        onContext={setContext}
        onPreset={applyPreset}
        branchId={branchFilter}
        branchName={selectedBranchName}
        onSeverity={setSeverity}
        onKind={setKind}
        onNewCode={setNewCode}
        onSoftwareQuality={setSoftwareQuality}
        onCleanCodeAttr={setCleanCodeAttr}
        onClearBranch={clearBranch}
      />

      <div className="mt-6">
        {error ? <RequestError error={error} /> : null}
        {!error && isLoading ? <Loading label="Loading issues…" /> : null}
        {!error && !isLoading && issues.length === 0 ? (
          <EmptyState hasFilters={hasActiveFilters} />
        ) : null}
        {!error && issues.length > 0 ? (
          <IssueList
            issues={issues}
            selectedID={selectedIssueID}
            onSelect={openIssue}
          />
        ) : null}

        {/* Infinite-scroll sentinel + footer status row. We always render
            the wrapper so the observer ref is stable across renders; the
            footer text changes based on paging state. */}
        <div
          ref={sentinelRef}
          className="
            mt-4 py-4 flex items-center justify-center gap-2
            text-xs text-fg-subtle
          "
        >
          {(() => {
            if (hasMore && isLoadingMore) return (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                <span>Loading more…</span>
              </>
            );
            if (hasMore) return <span>Scroll for more</span>;
            if (issues.length > 0) return <span>End of results.</span>;
            return null;
          })()}
        </div>
      </div>

      <IssueDetailDrawer
        issue={selectedIssue}
        requestedID={selectedIssueID || undefined}
        onClose={closeIssue}
      />

      {showImport ? (
        <ExternalIssueImportDialog
          projectID={id}
          onClose={() => setShowImport(false)}
          onImported={() => {
            // Re-fetch all pages so imported issues appear in the list.
            setSize(1);
            // Keep the dialog open so the success message is visible.
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb
// ---------------------------------------------------------------------------

function BackToProject({
  projectID,
  projectName,
}: {
  readonly projectID: string;
  readonly projectName?: string;
}) {
  return (
    <Link
      to={`/projects/${projectID}`}
      className="
        inline-flex items-center gap-1.5 mb-5 text-xs font-semibold
        uppercase tracking-[0.08em] text-fg-muted hover:text-fg
        transition-colors
      "
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      <span>{projectName ? `Back to ${projectName}` : 'Back to project'}</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

/** T592: named one-click filter combinations. */
export type IssuePreset = 'maintainability' | 'new-code-smells';

interface FilterBarProps {
  readonly severity?: IssueSeverity;
  readonly kind?: IssueKind;
  readonly newCode: IssueNewCodeFilter;
  /** T253: software quality filter. */
  readonly softwareQuality?: SoftwareQuality;
  /** T253: clean-code attribute filter. */
  readonly cleanCodeAttr?: CleanCodeAttribute;
  /** T9079: context facet (default production). */
  readonly context: IssueContext;
  readonly onContext: (next: IssueContext) => void;
  /** Branch ID filter (T058). Empty string means no branch filter. */
  readonly branchId: string;
  /** Human-readable name for the selected branch, null while loading. */
  readonly branchName: string | null;
  readonly onSeverity: (next: IssueSeverity | undefined) => void;
  readonly onKind: (next: IssueKind | undefined) => void;
  readonly onNewCode: (next: IssueNewCodeFilter) => void;
  readonly onSoftwareQuality: (next: SoftwareQuality | undefined) => void;
  readonly onCleanCodeAttr: (next: CleanCodeAttribute | undefined) => void;
  /** T592: apply a named preset filter combination. */
  readonly onPreset: (preset: IssuePreset) => void;
  readonly onClearBranch: () => void;
}

function FilterBar({
  severity,
  kind,
  newCode,
  softwareQuality,
  cleanCodeAttr,
  context,
  onContext,
  branchId,
  branchName,
  onSeverity,
  onKind,
  onNewCode,
  onSoftwareQuality,
  onCleanCodeAttr,
  onPreset,
  onClearBranch,
}: FilterBarProps) {
  // T592: a preset is "active" when the current filter state exactly matches
  // the combination it applies — lets the chip highlight like any other
  // toggle even though it sets three params at once.
  const maintainabilityActive =
    softwareQuality === 'Maintainability' && kind === undefined && newCode === 'all';
  const newCodeSmellsActive = kind === 'CODE_SMELL' && newCode === 'new';

  return (
    <section
      aria-label="Issue filters"
      className="orb-card p-4 flex flex-wrap items-center gap-x-6 gap-y-3"
    >
      <div className="inline-flex items-center gap-1.5 text-fg-muted">
        <Filter className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="orb-section-title">Filters</span>
      </div>

      <FilterGroup label="Presets">
        <ChipBtn
          active={maintainabilityActive}
          label="Maintainability"
          toneActive="bg-accent/15 text-accent border-accent/40"
          onClick={() => onPreset('maintainability')}
          icon={<Wrench className="h-3 w-3" aria-hidden="true" />}
          title="Issues affecting the Maintainability software quality"
        />
        <ChipBtn
          active={newCodeSmellsActive}
          label="New code smells"
          toneActive="bg-accent/15 text-accent border-accent/40"
          onClick={() => onPreset('new-code-smells')}
          icon={<Sprout className="h-3 w-3" aria-hidden="true" />}
          title="Code smells introduced in the current branch"
        />
      </FilterGroup>

      {branchId ? (
        <FilterGroup label="Branch">
          <button
            type="button"
            onClick={onClearBranch}
            className="
              orb-badge border bg-accent-muted text-accent border-accent/30
              cursor-pointer hover:opacity-70 transition-opacity
              inline-flex items-center gap-1.5
            "
            title="Clear branch filter"
            aria-label={`Remove branch filter: ${branchName ?? branchId}`}
          >
            <GitBranch className="h-3 w-3" aria-hidden="true" />
            <span>{branchName ?? branchId.slice(0, 8)}</span>
            <span aria-hidden="true" className="ml-0.5 font-bold">×</span>
          </button>
        </FilterGroup>
      ) : null}

      <FilterGroup label="Severity">
        {ISSUE_SEVERITIES.map((s) => (
          <SeverityChip
            key={s}
            value={s}
            active={severity === s}
            onClick={() => onSeverity(severity === s ? undefined : s)}
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Kind">
        {ISSUE_KINDS.map((k) => (
          <KindChip
            key={k}
            value={k}
            active={kind === k}
            onClick={() => onKind(kind === k ? undefined : k)}
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Scope">
        {(['all', 'new', 'overall'] as const).map((v) => (
          <NewCodeChip
            key={v}
            value={v}
            active={newCode === v}
            onClick={() => onNewCode(v)}
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Context">
        {ISSUE_CONTEXTS.map((c) => (
          <ChipBtn
            key={c}
            active={context === c}
            label={c.charAt(0).toUpperCase() + c.slice(1)}
            toneActive="bg-accent/15 text-accent border-accent/40"
            onClick={() => onContext(c)}
            icon={null}
            title={
              c === 'all'
                ? 'Findings in every kind of file'
                : `Only findings in ${c} files (default: production)`
            }
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Quality">
        {SOFTWARE_QUALITIES.map((q) => (
          <SoftwareQualityChip
            key={q}
            value={q}
            active={softwareQuality === q}
            onClick={() => onSoftwareQuality(softwareQuality === q ? undefined : q)}
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Attribute">
        {CLEAN_CODE_ATTRIBUTES.map((a) => (
          <CleanCodeAttrChip
            key={a}
            value={a}
            active={cleanCodeAttr === a}
            onClick={() => onCleanCodeAttr(cleanCodeAttr === a ? undefined : a)}
          />
        ))}
      </FilterGroup>
    </section>
  );
}

function FilterGroup({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="orb-section-title">{label}</span>
      <div className="flex items-center gap-1.5 flex-wrap">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

interface ChipBtnProps {
  readonly active: boolean;
  readonly label: string;
  /** Tone-specific tailwind classes layered on top of the base chip. */
  readonly toneActive: string;
  readonly toneIdle?: string;
  readonly onClick: () => void;
  readonly title?: string;
  readonly icon?: ReactNode;
}

function ChipBtn({
  active,
  label,
  toneActive,
  toneIdle,
  onClick,
  title,
  icon,
}: ChipBtnProps) {
  const idle =
    toneIdle ??
    'bg-bg-inset text-fg-muted border-border hover:text-fg hover:bg-bg-hover';
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={title}
      className={[
        'orb-badge border cursor-pointer transition-colors',
        active ? toneActive : idle,
      ].join(' ')}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

/** Severity tone palette. The same tones are reused by the row badge so a
 *  chip in the filter bar and the same severity on a row look identical. */
const SEVERITY_TONE: Record<IssueSeverity, string> = {
  INFO: 'bg-bg-inset text-fg-muted border-border',
  MINOR: 'bg-accent-muted text-accent border-accent/30',
  MAJOR: 'bg-warning/10 text-warning border-warning/40',
  CRITICAL: 'bg-danger/10 text-danger border-danger/40',
  BLOCKER: 'bg-danger/20 text-danger border-danger/60',
};

function SeverityChip({
  value,
  active,
  onClick,
}: {
  readonly value: IssueSeverity;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <ChipBtn
      active={active}
      label={value}
      toneActive={SEVERITY_TONE[value]}
      onClick={onClick}
      title={`Filter by ${value.toLowerCase()} severity`}
    />
  );
}

const KIND_LABEL: Record<IssueKind, string> = {
  BUG: 'Bug',
  CODE_SMELL: 'Smell',
  VULNERABILITY: 'Vuln',
  SECURITY_HOTSPOT: 'Hotspot',
  SECRET: 'Secret',
};

const KIND_ICON: Record<IssueKind, ReactNode> = {
  BUG: <Bug className="h-3 w-3" aria-hidden="true" />,
  CODE_SMELL: <Sprout className="h-3 w-3" aria-hidden="true" />,
  VULNERABILITY: <ShieldAlert className="h-3 w-3" aria-hidden="true" />,
  SECURITY_HOTSPOT: <Eye className="h-3 w-3" aria-hidden="true" />,
  SECRET: <KeyRound className="h-3 w-3" aria-hidden="true" />,
};

function KindChip({
  value,
  active,
  onClick,
}: {
  readonly value: IssueKind;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <ChipBtn
      active={active}
      label={KIND_LABEL[value]}
      toneActive="bg-nebula/15 text-nebula border-nebula/40"
      onClick={onClick}
      icon={KIND_ICON[value]}
      title={`Filter by ${value.replace('_', ' ').toLowerCase()}`}
    />
  );
}

function NewCodeChip({
  value,
  active,
  onClick,
}: {
  readonly value: IssueNewCodeFilter;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  let label: string;
  if (value === 'all') {
    label = 'All';
  } else if (value === 'new') {
    label = 'New code';
  } else {
    label = 'Overall code';
  }

  let chipTitle: string;
  if (value === 'all') {
    chipTitle = 'Show all issues';
  } else if (value === 'new') {
    chipTitle = 'Only issues introduced in the current branch';
  } else {
    chipTitle = 'Only issues carried over from prior scans';
  }

  return (
    <ChipBtn
      active={active}
      label={label}
      toneActive="bg-accent/15 text-accent border-accent/40"
      onClick={onClick}
      icon={value === 'new' ? <Sparkles className="h-3 w-3" aria-hidden="true" /> : null}
      title={chipTitle}
    />
  );
}

/** Visual tone per software quality (T253). */
const SOFTWARE_QUALITY_TONE: Record<SoftwareQuality, string> = {
  Security: 'bg-danger/10 text-danger border-danger/40',
  Reliability: 'bg-warning/10 text-warning border-warning/40',
  Maintainability: 'bg-accent/10 text-accent border-accent/40',
};

function SoftwareQualityChip({
  value,
  active,
  onClick,
}: {
  readonly value: SoftwareQuality;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <ChipBtn
      active={active}
      label={value}
      toneActive={SOFTWARE_QUALITY_TONE[value]}
      onClick={onClick}
      title={`Filter by ${value} quality`}
    />
  );
}

/** Visual tone per clean-code attribute (T253). */
const CLEAN_CODE_ATTR_TONE: Record<CleanCodeAttribute, string> = {
  Consistency: 'bg-nebula/15 text-nebula border-nebula/40',
  Intentionality: 'bg-accent/15 text-accent border-accent/40',
  Adaptability: 'bg-warning/10 text-warning border-warning/40',
  Responsibility: 'bg-danger/10 text-danger border-danger/40',
};

function CleanCodeAttrChip({
  value,
  active,
  onClick,
}: {
  readonly value: CleanCodeAttribute;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <ChipBtn
      active={active}
      label={value}
      toneActive={CLEAN_CODE_ATTR_TONE[value]}
      onClick={onClick}
      title={`Filter by ${value} clean-code attribute`}
    />
  );
}

// ---------------------------------------------------------------------------
// Issue list
// ---------------------------------------------------------------------------

function IssueList({
  issues,
  selectedID,
  onSelect,
}: {
  readonly issues: Issue[];
  readonly selectedID: string;
  readonly onSelect: (id: string) => void;
}) {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleCheck = (id: string, on: boolean) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleBulkJira = async () => {
    if (checked.size === 0) return;
    setBulkBusy(true);
    setBulkStatus(null);
    try {
      const res = await jiraApi.bulkCreate(Array.from(checked));
      const ok = res.results.filter((r) => !r.error).length;
      const fail = res.results.filter((r) => Boolean(r.error)).length;
      setBulkStatus(`${ok} ticket(s) created${fail > 0 ? `, ${fail} failed` : ''}.`);
      setChecked(new Set());
    } catch (e) {
      setBulkStatus(
        e instanceof ApiError ? `${e.code}: ${e.message}` : 'Bulk Jira creation failed.',
      );
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div>
      {checked.size > 0 && (
        <div className="flex items-center gap-3 px-5 py-2 bg-accent/5 border border-accent/20 rounded-t-lg mb-0">
          <span className="text-xs text-fg-muted">{checked.size} selected</span>
          <button
            type="button"
            onClick={handleBulkJira}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-border text-fg-muted hover:text-fg hover:border-fg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {bulkBusy ? <><Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> Creating…</> : 'Create Jira tickets'}
          </button>
          <button
            type="button"
            onClick={() => setChecked(new Set())}
            className="text-xs text-fg-muted hover:text-fg transition-colors"
          >
            Clear
          </button>
        </div>
      )}
      {bulkStatus && (
        <p className="text-xs text-fg-muted px-5 py-1">{bulkStatus}</p>
      )}
      <ul className="orb-card divide-y divide-border" aria-label="Issues">
        {issues.map((iss) => (
          <IssueRow
            key={iss.id}
            issue={iss}
            selected={iss.id === selectedID}
            checked={checked.has(iss.id)}
            onCheck={toggleCheck}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </div>
  );
}

function IssueRow({
  issue,
  selected,
  checked,
  onCheck,
  onSelect,
}: {
  readonly issue: Issue;
  readonly selected: boolean;
  readonly checked: boolean;
  readonly onCheck: (id: string, on: boolean) => void;
  readonly onSelect: (id: string) => void;
}) {
  const severity = normalizeSeverity(issue.severity);
  const kind = normalizeKind(issue.kind);
  const tone = severity
    ? SEVERITY_TONE[severity]
    : SEVERITY_TONE.INFO;
  const fileLine = issue.file_path
    ? `${issue.file_path}:${issue.line_start || 1}`
    : '—';
  // The row is a button — clicking the body opens the detail drawer.
  // Native <button> in <li> gives us focus ring + Enter/Space activation
  // for free (T033). We keep the visual treatment list-like, not button-
  // like, so the page still reads as a results list.
  return (
    <li className="flex items-start">
      {/* Checkbox for bulk Jira export */}
      <div className="flex items-center pl-3 pt-3.5 shrink-0">
        <input
          type="checkbox"
          aria-label={`Select issue ${issue.id}`}
          checked={checked}
          onChange={(e) => onCheck(issue.id, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          className="h-3.5 w-3.5 accent-accent cursor-pointer"
        />
      </div>
      <button
        type="button"
        onClick={() => onSelect(issue.id)}
        aria-pressed={selected}
        aria-label={`Open issue: ${issue.message || issue.rule_id || issue.id}`}
        className={[
          'flex-1 text-left px-3 py-3 text-sm',
          'transition-colors hover:bg-bg-hover focus:outline-none focus:bg-bg-hover',
          'focus-visible:ring-2 focus-visible:ring-accent/40',
          selected ? 'bg-bg-hover' : '',
        ].join(' ')}
      >
        <div className="flex items-start gap-3">
          <span className={`orb-badge border shrink-0 ${tone}`}>
            {severity ?? issue.severity ?? 'INFO'}
          </span>
          {kind ? (
            <span
              className="orb-badge border bg-nebula/10 text-nebula border-nebula/30 shrink-0"
              title={kind.replace('_', ' ').toLowerCase()}
            >
              {KIND_ICON[kind]}
              <span>{KIND_LABEL[kind]}</span>
            </span>
          ) : null}
          {issue.is_new ? (
            <span
              className="
                orb-badge border bg-accent/15 text-accent border-accent/30
                shrink-0
              "
              title="Introduced in the current branch"
            >
              <Sparkles className="h-3 w-3" aria-hidden="true" />
              <span>new</span>
            </span>
          ) : null}
          {issue.source === 'external' ? (
            <span
              className="
                orb-badge border bg-warning/10 text-warning border-warning/30
                shrink-0
              "
              title="Imported from an external linter report"
            >
              <FileUp className="h-3 w-3" aria-hidden="true" />
              <span>external</span>
            </span>
          ) : null}
          <div className="flex-1 min-w-0">
            <p className="text-fg font-medium leading-snug">
              {issue.message || <span className="text-fg-subtle">(no message)</span>}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-subtle">
              <span className="orb-code text-xs truncate max-w-[28rem]">
                {fileLine}
              </span>
              {issue.rule_id ? (
                <span className="text-fg-muted">{issue.rule_id}</span>
              ) : null}
              {issue.detected_at ? (
                <span data-testid="issue-detected-at">{formatTimestamp(issue.detected_at)}</span>
              ) : null}
              {issue.resolution ? (
                <span className="text-fg-muted">resolution · {issue.resolution}</span>
              ) : null}
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Shared bits (loading / error / empty)
// ---------------------------------------------------------------------------

function Loading({ label }: { readonly label: string }) {
  return (
    <div className="orb-card p-6 flex items-center justify-center gap-3 text-fg-muted">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

function RequestError({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  const hint =
    error instanceof ApiError && error.code === 'missing_org'
      ? 'Set VITE_DEV_ORG_ID for local dev, or sign in — redirecting to /login…'
      : null;
  return (
    <div role="alert" className="orb-card p-5 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-1">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}

function EmptyState({ hasFilters }: { readonly hasFilters: boolean }) {
  if (hasFilters) {
    return (
      <div className="orb-card p-8 text-center">
        <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
          <FileWarning className="h-5 w-5" aria-hidden="true" />
        </div>
        <h2 className="font-display text-lg font-semibold text-fg mb-2">
          No issues match these filters.
        </h2>
        <p className="text-fg-muted text-sm max-w-md mx-auto">
          Loosen a filter or switch to the <span className="orb-code">All</span>{' '}
          scope to see more results.
        </p>
      </div>
    );
  }
  return (
    <div className="orb-card p-8 text-center">
      <div className="text-fg-subtle inline-flex items-center justify-center mb-2">
        <AlertTriangle className="h-5 w-5" aria-hidden="true" />
      </div>
      <h2 className="font-display text-lg font-semibold text-fg mb-2">
        No issues yet.
      </h2>
      <p className="text-fg-muted text-sm max-w-md mx-auto">
        Issues land here once a scan finishes against this project. The Phase-1
        list endpoint is a stub — findings will appear once the handler
        delegates to <span className="orb-code">IssuesRepo.List</span>.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseSeverity(raw: string | null): IssueSeverity | undefined {
  if (!raw) return undefined;
  const up = raw.toUpperCase();
  return (ISSUE_SEVERITIES as readonly string[]).includes(up)
    ? (up as IssueSeverity)
    : undefined;
}

function parseKind(raw: string | null): IssueKind | undefined {
  if (!raw) return undefined;
  const up = raw.toUpperCase();
  return (ISSUE_KINDS as readonly string[]).includes(up)
    ? (up as IssueKind)
    : undefined;
}

function parseContext(raw: string | null): IssueContext {
  const candidates: readonly string[] = ISSUE_CONTEXTS;
  const match = raw ? candidates.find((c) => c === raw.toLowerCase()) : undefined;
  return (match as IssueContext | undefined) ?? 'production';
}

function parseNewCode(raw: string | null): IssueNewCodeFilter {
  if (raw === 'new' || raw === 'overall') return raw;
  return 'all';
}

function parseSoftwareQuality(raw: string | null): SoftwareQuality | undefined {
  if (!raw) return undefined;
  const candidates: readonly string[] = SOFTWARE_QUALITIES;
  const match = candidates.find((q) => q.toLowerCase() === raw.toLowerCase());
  return match as SoftwareQuality | undefined;
}

function parseCleanCodeAttr(raw: string | null): CleanCodeAttribute | undefined {
  if (!raw) return undefined;
  const candidates: readonly string[] = CLEAN_CODE_ATTRIBUTES;
  const match = candidates.find((a) => a.toLowerCase() === raw.toLowerCase());
  return match as CleanCodeAttribute | undefined;
}

function normalizeSeverity(raw: string): IssueSeverity | undefined {
  return parseSeverity(raw);
}

function normalizeKind(raw: string): IssueKind | undefined {
  return parseKind(raw);
}

/**
 * Format a remediation-effort value in minutes into a human person-time
 * label. Same minute/hour/day/week scale as ProjectDetail.tsx's
 * `formatDebt` (SQALE technical-debt display) — kept as a local copy since
 * the two pages don't share a utils module (T592).
 */
function formatEffort(minutes: number): string {
  if (minutes <= 0) return '0min';
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  if (h < 8) return `${h}h`;
  const d = Math.floor(h / 8);
  if (d < 5) return `${d}d`;
  return `${Math.floor(d / 5)}w`;
}

/**
 * Format an ISO-8601 timestamp as a compact local string. Same convention as
 * ProjectDetail.tsx — we avoid relative formats so long-open tabs don't drift.
 */
function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
